/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subscription, combineLatest, distinctUntilKeyChanged } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import {
  LoadContentStoreFrameworks,
  LoadFrameworkCategories,
  LoadFrameworkDescendants,
  LoadFrameworkTagDescendants,
  LoadFrameworks,
  LoadLtiFrameworks,
  RemoveSelectedFrameworkFromStore,
} from '../../../page-modules/admin/store/framework.actions';
import { FrameWorkState } from '../../../page-modules/admin/store/framework.state';
import { UserDetails } from '../../../user-auth/models';
import { LTI_AUTH_SERVICE, LtiAuthService } from '../../../user-auth/services/lti-auth.service';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';
import { Organization, ResourceTag } from '../../models';
import { Framework, FrameworkTag, FrameworkType } from '../../models/admin/admin.model';
import { LoadableState } from '../../store';
import { FrameworkCreateComponent } from './create-framework/framework-create.component';
import { TagChangeEmitterData } from './framework-tags-list/framework-tags-list.component';
import { TagsList } from './frameworks-models/tag-list.model';

export type ResourceTagWithCompetencyLevel = ResourceTag & {
  selectedCompetencyLevel?: string;
};

@Component({
  selector: 'ptl-add-framework-tags',
  templateUrl: './add-framework-tags.component.html',
  styleUrls: ['./add-framework-tags.component.scss'],
})
export class AddFrameworkTagsComponent implements OnInit, OnDestroy {
  /** Is only in lti module */
  @Input() isLtiModule: boolean;
  /** Course-level mode - when true, requestId is not sent to API */
  @Input() isCourseLevelMode: boolean;
  /** Is dialog container or static folio page */
  @Input() momentTags: ResourceTag[];
  /** Is dialog container or static folio page */
  @Input() isDialogPage: boolean;
  /** Is only a single selection allowed */
  @Input() singleSelection: boolean;
  /** Only framework can be selected */
  @Input() selectFrameworks: boolean;
  /** Only content store tags can be selected */
  @Input() selectContentStoreTags: boolean;
  /** Only discovery tags can be selected */
  @Input() selectDiscoveryTags: boolean;
  /** Selected Tag Ids for default select */
  @Input() selectedTagIds: string[];
  /** Disable flag for checkboxes */
  @Input() isTagDisabled = false;
  /** Show admin actions */
  @Input() type: 'DEFAULT' | 'ADMIN' = 'DEFAULT';
  /** Framework type to be managed */
  @Input() frameworkType: FrameworkType = 'STANDARD';

  /** Emits when a palette action was evoked. */
  @Output() addTagBulk = new EventEmitter<ResourceTagWithCompetencyLevel[]>();
  /** Emits when a palette action was evoked. */
  @Output() addFrameworkBulk = new EventEmitter<Framework[]>();

  /** Emits event to close framework tags table. */
  @Output() closeFrameworkTags = new EventEmitter<boolean>();

  @Select(FrameWorkState.frameworks)
  frameworks$: Observable<LoadableState<Framework[]>>;

  @Select(FrameWorkState.frameworkDescendants)
  frameworkDescendants$: Observable<LoadableState<ResourceTag[]>>;

  @Select(FrameWorkState.tagDescendants)
  tagDescendants$: Observable<LoadableState<ResourceTag[]>>;

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  @Select(UserAuthState.userHasSuperAdminRole)
  isUserSuperAdmin$: Observable<boolean>;

  @Select(UserAuthState.userHasAdminRole)
  isUserAdmin$: Observable<boolean>;

  @ViewChild('frameworkCreateComponent') frameworkCreateComponent: FrameworkCreateComponent;

  frameworks: Framework[];
  frameworkTags: ResourceTag[];
  nestedTags: ResourceTag[];
  isCreateFrameworkState = false;

  selectedFramework: Framework = null;

  selectedFrameworkId$ = new BehaviorSubject<string | undefined>(undefined);
  set selectedFrameworkId(id: string) {
    this.selectedFrameworkId$.next(id);
  }
  get selectedFrameworkId(): string {
    return this.selectedFrameworkId$.value;
  }

  selectedTags: ResourceTagWithCompetencyLevel[] = [];
  selectedFrameworks: Framework[] = [];
  opened = false;

  private tagsHierarchyIds: string[] = [];
  private loadedTags: { [uid: string]: (ResourceTag & { checked?: boolean })[] } = {};
  private frameworksSubscription: Subscription;
  private frameworkTagsSubscription: Subscription;
  private subscriptionEnd$ = new EventEmitter<void>();
  private selectLastCreatedFramework: boolean;

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    @Inject(LTI_AUTH_SERVICE) private ltiAuthService: LtiAuthService,
  ) {}

  ngOnInit(): void {
    this.loadFrameworks();

    this.setFrameworksData();
    if (!this.selectFrameworks) {
      this.subscribeToFrameworkTags();
    }
  }

  ngOnDestroy() {
    this.frameworkTagsSubscription?.unsubscribe();
    this.frameworksSubscription?.unsubscribe();
    this.subscriptionEnd$?.emit();
  }

  onLoadFrameworkTags(frameworkId: string) {
    this.isCreateFrameworkState = false;
    this.cd.detectChanges();

    this.selectedFramework = this.frameworks.find((item) => {
      return item.uid === frameworkId;
    });
    this.selectedFrameworkId = frameworkId;
    if (!this.isLtiModule) {
      this.store.dispatch(new LoadFrameworkDescendants(frameworkId));
    }
    this.resetTagsHierarchy();
  }

  onFrameworkSelected(framework: Framework) {
    if (this.singleSelection) {
      this.selectedFrameworks.push(framework);
      this.onAddFramework();
    } else {
      if (framework.active) {
        this.selectedFrameworks.push(framework);
      } else {
        this.selectedFrameworks = this.selectedFrameworks.filter((item) => item._id !== framework._id);
      }
    }
  }

  onCheckboxChange(event: TagChangeEmitterData) {
    const index = this.selectedTags.findIndex((tag) => tag._id === event.tag._id);
    if (event.checked) {
      const tag = {
        _id: event.tag._id, // TODO will remove in feature
        id: event.tag._id,
        title: event.tag.title,
        selectedCompetencyLevel: event.selectedCompetencyLevel,
      };
      if (index === -1) {
        this.selectedTags.push(tag);
      } else {
        this.selectedTags[index] = tag;
      }
    } else {
      if (index !== -1) {
        this.selectedTags.splice(index, 1);
      }
    }

    this.selectedTags = this.makeArrayUniqueByProperty(this.selectedTags, '_id');

    if (this.singleSelection) {
      this.onAddTag();
    }
  }

  onCancel() {
    this.closeFrameWorkPanel();
    if (this.frameworkType === 'CATEGORIES') {
      this.isCreateFrameworkState = false;
    }
  }

  onAddTag() {
    this.addTagBulk.emit(this.selectedTags.map((tag) => ({ ...tag, frameworkId: this.selectedFrameworkId })));
    this.closeFrameWorkPanel();
  }

  onAddFramework() {
    if (this.selectedFrameworks && this.selectedFrameworks.length) {
      this.addFrameworkBulk.emit(this.selectedFrameworks);
      this.closeFrameWorkPanel();
    }
  }

  onLoadNestedTags(tagId: string) {
    if (!this.isLtiModule) {
      this.store
        .dispatch(new LoadFrameworkTagDescendants(this.selectedFrameworkId, tagId))
        .toPromise()
        .then(() => {
          this.tagDescendants$
            .pipe(
              filter(({ loading, data }) => !loading && !!data),
              map(({ data }) => data),
            )
            .subscribe((tags) => {
              if (tags.length) {
                this.nestedTags = this.setAndGetLoadedTags(tagId, tags);
                this.tagsHierarchyIds.push(tagId);
              }
            })
            .unsubscribe();
        });
    } else {
      const findTag = (tags: FrameworkTag[]): FrameworkTag | undefined => {
        for (const tag of tags) {
          if (tag.tagId === tagId) return tag;
          if (tag.children) {
            const result = findTag(tag.children);
            if (result) return result;
          }
        }
        return undefined;
      };

      const newParentTag = findTag(this.selectedFramework.tags);
      if (!newParentTag) {
        console.error(`Tag ${tagId} not found`);
        return;
      }

      this.nestedTags = this.setAndGetLoadedTags(
        tagId,
        this.mapFrameworkTagsToResourceTags(this.selectedFrameworkId, newParentTag.children || []),
      );
      this.tagsHierarchyIds.push(tagId);
    }
  }

  onNestedTagsBack() {
    this.tagsHierarchyIds.pop();
    const previousTagId = this.tagsHierarchyIds[this.tagsHierarchyIds.length - 1];

    if (!previousTagId) {
      this.nestedTags = undefined;
    } else {
      this.nestedTags = this.loadedTags[previousTagId];
    }
  }

  onOpenAddFrameworkView() {
    this.selectedFrameworkId = null;
    this.selectedFramework = null;
    this.isCreateFrameworkState = true;
    this.cd.detectChanges();
  }

  onCreateFramework() {
    this.frameworkCreateComponent.onAddFramework();
  }

  onUpdateFramework() {
    this.frameworkCreateComponent.onUpdateFramework();
    if (this.frameworkType === 'CATEGORIES') {
      this.isCreateFrameworkState = false;
    }
  }

  onEditTag() {
    this.isCreateFrameworkState = true;
    this.cd.detectChanges();
  }

  onFrameworkCreated() {
    this.selectLastCreatedFramework = true;
  }

  closeComponent(): void {
    this.closeFrameworkTags.emit(false);
  }

  private loadFrameworks() {
    if (!this.isLtiModule) {
      if (this.selectContentStoreTags) {
        this.store.dispatch(new LoadContentStoreFrameworks());
      } else if (this.selectDiscoveryTags) {
        this.store.dispatch(new LoadFrameworkCategories());
      } else if (this.frameworkType === 'CATEGORIES') {
        this.store.dispatch(new LoadFrameworkCategories());
      } else {
        this.store.dispatch(new LoadFrameworks());
      }

      return;
    }

    this.organizationData$.pipe(takeUntil(this.subscriptionEnd$), distinctUntilKeyChanged('_id')).subscribe((org) => {
      const organizationUid = org._id;
      // In course-level mode, don't send requestId (API will use JWT auth)
      // In deep-linking mode, use requestId from localStorage
      const requestId = this.isCourseLevelMode ? null : this.ltiAuthService.getLtiRequestId();

      this.store.dispatch(new LoadLtiFrameworks(organizationUid, requestId));
    });
  }

  private makeArrayUniqueByProperty(arr: TagsList[], property: string): TagsList[] {
    const uniqueProps: Set<keyof TagsList> = new Set();
    return arr.filter((obj: TagsList) => {
      if (uniqueProps.has(obj[property])) {
        return false;
      }
      uniqueProps.add(obj[property]);
      return true;
    });
  }

  private setAndGetLoadedTags(key: string, tags: (ResourceTag & { checked?: boolean })[]) {
    if (!this.loadedTags[key]) {
      this.loadedTags[key] = tags;
      return this.loadedTags[key];
    } else {
      for (const item of this.loadedTags[key]) {
        tags.map((tag) => {
          if (item._id === tag._id) {
            tag.checked = item.checked;
          }
          return tag;
        });
      }
      this.loadedTags[key] = tags;
      return this.loadedTags[key];
    }
  }

  private resetTagsHierarchy() {
    this.nestedTags = undefined;
    this.tagsHierarchyIds = [];
  }

  private setFrameworksData() {
    this.frameworksSubscription = this.frameworks$
      .pipe(
        filter(({ loading, data }) => !loading && !!data),
        map(({ data }) =>
          data.map((framework: Framework) => {
            return framework;
          }),
        ),
      )
      .subscribe((frameworks) => {
        this.frameworks = frameworks;
        this.opened = true;
        this.triggerFrameworkItemClick();
      });
  }

  private triggerFrameworkItemClick() {
    if (this.selectLastCreatedFramework) {
      setTimeout(() => {
        const frameworksList = document.querySelectorAll('.f_framework-list-item');

        if (frameworksList) {
          const lastElement = frameworksList[frameworksList.length - 1] as HTMLElement;

          if (lastElement) {
            lastElement.click();
            lastElement.scrollIntoView();
          }
        }
      }, 0);
    }

    if (this.selectedFrameworkId) {
      setTimeout(() => {
        const element = document.querySelector('.f_framework-item-' + this.selectedFrameworkId);
        (element as HTMLElement)?.click();
        element?.scrollIntoView();
      }, 0);
    }
  }

  private subscribeToFrameworkTags() {
    if (!this.isLtiModule) {
      this.frameworkTagsSubscription = this.frameworkDescendants$
        .pipe(
          takeUntil(this.subscriptionEnd$),
          filter(({ loading, data }) => !loading && !!data),
          map(({ data }) => data),
        )
        .subscribe((frameworkTags) => {
          this.frameworkTags = this.setAndGetLoadedTags(this.selectedFrameworkId, frameworkTags);
        });
    } else {
      this.frameworkTagsSubscription = combineLatest([this.selectedFrameworkId$, this.frameworks$])
        .pipe(
          takeUntil(this.subscriptionEnd$),
          filter(([frameworkId, frameworks]) => frameworkId && !frameworks.loading && !!frameworks.data),
          map(
            ([frameworkId, { data: frameworks }]) =>
              [frameworkId, frameworks.find((f) => f._id === frameworkId || f.uid === frameworkId)] as [string, Framework],
          ),
          filter(([_, framework]) => !!framework),
        )
        .subscribe(([frameworkId, framework]) => {
          this.frameworkTags = this.setAndGetLoadedTags(frameworkId, this.mapFrameworkTagsToResourceTags(frameworkId, framework.tags));
        });
    }
  }

  private mapFrameworkTagsToResourceTags(frameworkId: string, tags: FrameworkTag[]): ResourceTag[] {
    return tags.map((t) => ({
      ...t,
      _id: t.tagId,
      frameworkId,
      descendantsCount: t.children?.length || 0,
    }));
  }

  private closeFrameWorkPanel() {
    this.closeFrameworkTags.emit(false);
    this.removeFrameworkFromStore();
  }

  private removeFrameworkFromStore() {
    this.store.dispatch(new RemoveSelectedFrameworkFromStore());
    if (this.frameworkType === 'CATEGORIES' && this.type === 'ADMIN') {
      this.store.dispatch(new LoadFrameworkCategories());
    }
  }
}
